import type {BoardData} from '@github-ui/board-core/types'
import {SafeHTML, Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

/**
 * Contract scaffolding for M1: what the Rails serialiser produces and the browser maps into
 * `BoardData`. Pinned by `__tests__/fixtures/board-payload.json`, whose sibling copy in `github/github`
 * is kept in sync by hand — a shape change on either side must be mirrored in the other's
 * fixture.
 * This should eventually be replaced by Protos https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/e2e-types/.
 */

// --- Enums ---------------------------------------------------------------

const IssueStateSchema = Type.Union([Type.Literal('open'), Type.Literal('closed')])
export type IssueState = Type.Static<typeof IssueStateSchema>

const IssueStateReasonSchema = Type.Union([
  Type.Literal('not_planned'),
  Type.Literal('completed'),
  Type.Literal('reopened'),
  Type.Literal('duplicate'),
])
export type IssueStateReason = Type.Static<typeof IssueStateReasonSchema>

const PullRequestStateSchema = Type.Union([
  Type.Literal('draft'),
  Type.Literal('open'),
  Type.Literal('closed'),
  Type.Literal('merged'),
  Type.Literal('queued'),
  Type.Literal('archived'),
])
export type PullRequestState = Type.Static<typeof PullRequestStateSchema>

// --- Leaf entities -------------------------------------------------------

/** Author and assignee share a shape: both carry the agent/copilot flags. `isCopilot` implies `isAgent`. */
const ActorSchema = Type.Object({
  login: Type.String(),
  name: Type.Union([Type.String(), Type.Null()]),
  avatarUrl: Type.String(),
  isAgent: Type.Boolean(),
  isCopilot: Type.Boolean(),
})
export type BoardPayloadAuthor = Type.Static<typeof ActorSchema>
export type BoardPayloadAssignee = Type.Static<typeof ActorSchema>

const LabelSchema = Type.Object({
  id: Type.Number(),
  name: Type.String(),
  nameHtml: Type.String(),
  color: Type.String(),
  description: Type.Union([Type.String(), Type.Null()]),
})
export type BoardPayloadLabel = Type.Static<typeof LabelSchema>

const MilestoneSchema = Type.Object({
  title: Type.String(),
  url: Type.String(),
  state: Type.Union([Type.Literal('open'), Type.Literal('closed')]),
  dueOn: Type.Union([Type.String(), Type.Null()]),
})
export type BoardPayloadMilestone = Type.Static<typeof MilestoneSchema>

const IssueTypeSchema = Type.Object({name: Type.String(), color: Type.String()})
export type BoardPayloadIssueType = Type.Static<typeof IssueTypeSchema>

const ParentIssueSchema = Type.Object({
  titleHtml: Type.String(),
  number: Type.Number(),
  state: IssueStateSchema,
  stateReason: Type.Union([IssueStateReasonSchema, Type.Null()]),
  url: Type.String(),
  updatedAt: Type.String(),
})
export type BoardPayloadParentIssue = Type.Static<typeof ParentIssueSchema>

// --- Field slots ---------------------------------------------------------

/**
 * Field slots keyed by the registered identifier from `identifiers-findings.md`. This is
 * the object the card joins against the field descriptor: `descriptor.fields[f].identifier
 * → item.fields[<same identifier>].value`.
 *
 * **Keys are the identifier verbatim** — no camelCase, snake_case, plural, hyphen, or
 * alias transform. The identifier is simultaneously the URL token, sort token, descriptor
 * key, and payload key; they must be one string so `descriptor.fields[f]` and
 * `item.fields[f]` line up (D26 / github/views#55).
 *
 * **Every slot is `Type.Optional` for M1.** This contract is hand-synced with Rails.
 * Optional starts loose and can be tightened per-field once a captured fixture proves
 * the field is always sent. Absent key = "producer didn't deliver"; present with
 * `{value: []}` or `{value: null}` = "delivered, no value" — a card with no labels
 * still ships `label: {value: []}`.
 */
const LabelFieldSchema = Type.Object({value: Type.Array(LabelSchema)})
export type LabelField = Type.Static<typeof LabelFieldSchema>

const MilestoneFieldSchema = Type.Object({value: Type.Union([MilestoneSchema, Type.Null()])})
export type MilestoneField = Type.Static<typeof MilestoneFieldSchema>

const IssueTypeFieldSchema = Type.Object({value: Type.Union([IssueTypeSchema, Type.Null()])})
export type IssueTypeField = Type.Static<typeof IssueTypeFieldSchema>

const ParentIssueFieldSchema = Type.Object({value: Type.Union([ParentIssueSchema, Type.Null()])})
export type ParentIssueField = Type.Static<typeof ParentIssueFieldSchema>

const CreatedFieldSchema = Type.Object({value: Type.String()})
export type CreatedField = Type.Static<typeof CreatedFieldSchema>

const UpdatedFieldSchema = Type.Object({value: Type.String()})
export type UpdatedField = Type.Static<typeof UpdatedFieldSchema>

// Null until the item is closed or merged.
const ClosedFieldSchema = Type.Object({value: Type.Union([Type.String(), Type.Null()])})
export type ClosedField = Type.Static<typeof ClosedFieldSchema>

const AuthorFieldSchema = Type.Object({value: ActorSchema})
export type AuthorField = Type.Static<typeof AuthorFieldSchema>

// Multi-assignee: value is an array, empty when the item has no assignees.
const AssigneeFieldSchema = Type.Object({value: Type.Array(ActorSchema)})
export type AssigneeField = Type.Static<typeof AssigneeFieldSchema>

const IssueFieldsSchema = Type.Object({
  label: Type.Optional(LabelFieldSchema),
  milestone: Type.Optional(MilestoneFieldSchema),
  created: Type.Optional(CreatedFieldSchema),
  updated: Type.Optional(UpdatedFieldSchema),
  closed: Type.Optional(ClosedFieldSchema),
  author: Type.Optional(AuthorFieldSchema),
  assignee: Type.Optional(AssigneeFieldSchema),
  // Issue-only field.
  issue_type: Type.Optional(IssueTypeFieldSchema),
  // Also issue-only. Hyphen is verbatim from the registry — forces bracket access at reads.
  'parent-issue': Type.Optional(ParentIssueFieldSchema),
})
export type IssueFields = Type.Static<typeof IssueFieldsSchema>

const PullRequestFieldsSchema = Type.Object(
  {
    label: Type.Optional(LabelFieldSchema),
    milestone: Type.Optional(MilestoneFieldSchema),
    created: Type.Optional(CreatedFieldSchema),
    updated: Type.Optional(UpdatedFieldSchema),
    closed: Type.Optional(ClosedFieldSchema),
    author: Type.Optional(AuthorFieldSchema),
    assignee: Type.Optional(AssigneeFieldSchema),
  },
  {additionalProperties: false},
)

export type PullRequestFields = Type.Static<typeof PullRequestFieldsSchema>

// --- Item shape ----------------------------------------------------------

const ItemBaseSchema = Type.Object({
  /** Database id: unstable across hosts, kept for the client's own bookkeeping. */
  id: Type.Number(),
  /** Globally-unique identity; safe as a render or cache key across a mixed cell. */
  globalId: Type.String(),
  number: Type.Number(),
  title: Type.String(),
  titleHtml: Type.String(),
  permalink: Type.String(),
  repoNameWithOwner: Type.String(),
  /**
   * Whether this viewer may change the fields this board arranges by, on this item. One
   * boolean covering grouping and swimlane together, computed by the field layer, never
   * rebuilt in the browser from anything else.
   *
   * Optional so this client can be deployed before the server emits it, which is the order
   * the two are released in. Absent reads as `false`: a drag the server will refuse is worse
   * than no drag at all.
   */
  canMove: Type.Optional(Type.Boolean()),
})

const IssueItemSchema = Type.Intersect([
  ItemBaseSchema,
  Type.Object({
    type: Type.Literal('issue'),
    state: IssueStateSchema,
    stateReason: Type.Union([IssueStateReasonSchema, Type.Null()]),
    fields: IssueFieldsSchema,
  }),
])
export type BoardPayloadIssueItem = Type.Static<typeof IssueItemSchema>

const PullRequestItemSchema = Type.Intersect([
  ItemBaseSchema,
  Type.Object({
    type: Type.Literal('pull_request'),
    state: PullRequestStateSchema,
    fields: PullRequestFieldsSchema,
  }),
])
export type BoardPayloadPullRequestItem = Type.Static<typeof PullRequestItemSchema>

/** Issues and pull requests share a cell, discriminated by `type`. */
const ItemSchema = Type.Union([IssueItemSchema, PullRequestItemSchema])
export type BoardPayloadItem = Type.Static<typeof ItemSchema>

// --- Axes, cells, top-level ---------------------------------------------

/**
 * The key the server reserves for the bucket holding items with no value for an axis
 * (`Issues::Board::Payload::NO_VALUE_KEY`). It arrives under an empty label because the
 * wording is the client's (github/views#137); `to-board-data.ts` names it.
 */
export const NO_VALUE_KEY = '__NO_VALUE__'

/**
 * The role colours an axis bucket can be drawn in, verbatim from the server's
 * `IssueSearchField::OptionColor`. Named rather than a hex value so each one resolves to a
 * Primer role token and follows the reader's theme; `@github-ui/use-named-color` owns that
 * mapping and names the same eight in upper case.
 */
const AxisColorSchema = Type.Union([
  Type.Literal('gray'),
  Type.Literal('blue'),
  Type.Literal('green'),
  Type.Literal('yellow'),
  Type.Literal('orange'),
  Type.Literal('red'),
  Type.Literal('pink'),
  Type.Literal('purple'),
])
export type AxisColor = Type.Static<typeof AxisColorSchema>

/**
 * What a bucket is drawn in, for a field whose buckets carry a colour at all.
 *
 * Tagged rather than two optional members side by side, because the two forms are drawn
 * differently and only one follows the reader's theme: `named` is one of eight role names that
 * resolves to a Primer token, `hex` is the literal value an account stored for a label. Stating
 * which it is keeps a role name from being read as a literal, which would freeze it to whatever
 * the light theme resolves to today.
 */
const AxisBucketColorSchema = Type.Union([
  Type.Object({type: Type.Literal('named'), name: AxisColorSchema}),
  // Six lowercase hex digits with no leading `#`, which is how the server states a label's
  // colour and how a heading interpolates it into a style. Pinned here because an
  // unconstrained string reaches that style as invalid CSS rather than as a failed parse.
  Type.Object({type: Type.Literal('hex'), hex: Type.String({pattern: '^[0-9a-f]{6}$'})}),
])
export type BoardPayloadAxisColor = Type.Static<typeof AxisBucketColorSchema>

/**
 * Everything one axis bucket is drawn from: the text its heading reads, and whatever else the
 * field that produced the bucket gives the browser to draw it with.
 *
 * An object rather than members beside the key, because what a bucket needs varies by field: a
 * select option has one of eight role colours, a label has an arbitrary hex one and
 * server-rendered markup for its emoji, an assignee has an avatar.
 */
const AxisLabelSchema = Type.Object({
  text: Type.String(),
  /**
   * The heading as server-rendered markup, for a field whose names carry emoji. Absent where
   * there is nothing to draw, so a present value always means markup and `text` is what names
   * the bucket otherwise.
   *
   * `SafeHTMLString` because this is where the server's rendering enters the browser, and a
   * heading renders it as markup. Anything reaching a producer of this payload from anywhere
   * but the HTML pipeline belongs in `text`, which renders as text.
   */
  textHtml: Type.Optional(SafeHTML()),
  /** Drawn beside the text, for a field whose buckets carry a colour. */
  color: Type.Optional(AxisBucketColorSchema),
  /**
   * Drawn beside the text, for a field whose buckets are accounts. `text` is the login, so the
   * avatar adds nothing a screen reader needs.
   */
  avatarUrl: Type.Optional(Type.String()),
})
export type BoardPayloadAxisLabel = Type.Static<typeof AxisLabelSchema>

const ColumnSchema = Type.Object({
  key: Type.String(),
  label: AxisLabelSchema,
  /** The server's count for the whole column. Never a sum of its cells. */
  totalCount: Type.Number(),
})
export type BoardPayloadColumn = Type.Static<typeof ColumnSchema>

const RowSchema = Type.Object({
  key: Type.String(),
  label: AxisLabelSchema,
  totalCount: Type.Number(),
})
export type BoardPayloadRow = Type.Static<typeof RowSchema>

const CellSchema = Type.Object({
  columnKey: Type.String(),
  rowKey: Type.String(),
  /** The server's count for this cell. Never `items.length`. */
  totalCount: Type.Number(),
  /**
   * Whether the cards were fetched for this cell. Counts cover the whole loaded grid while cards
   * stop at the region the board named, so `false` leaves `items`, `hasMoreItems` and
   * `itemsCursor` saying nothing about it: counted, never asked for (github/views#192). Optional
   * until the board has an affordance that consumes it.
   */
  itemsLoaded: Type.Optional(Type.Boolean()),
  hasMoreItems: Type.Boolean(),
  /** Opaque cursor reserved for cell pagination, which the board does not consume yet. */
  itemsCursor: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  items: Type.Array(ItemSchema),
})
export type BoardPayloadCell = Type.Static<typeof CellSchema>

/**
 * `Issues::Board::Payload::CellPage`. Carries no count: a board's counts are settled by the
 * request that drew it, so a page restating one would let a heading drift from its grid
 * (github/views#20).
 */
const CellPageSchema = Type.Object({
  columnKey: Type.String(),
  rowKey: Type.String(),
  items: Type.Array(ItemSchema),
  hasMoreItems: Type.Boolean(),
  itemsCursor: Type.Union([Type.String(), Type.Null()]),
})
export type BoardCellPage = Type.Static<typeof CellPageSchema>

const boardCellPageValidator = Compile(CellPageSchema)

/**
 * Beyond the shape, holds the two fields to each other: a cursor is minted exactly when there
 * is more to reach, so a page claiming more without one is a cell nothing could finish reading.
 * The server raises rather than serializing either, so this catches contract drift.
 */
export function isBoardCellPage(value: unknown): value is BoardCellPage {
  if (!boardCellPageValidator.Check(value)) return false

  return value.hasMoreItems === (value.itemsCursor !== null)
}

export const BoardInitialPayloadSchema = Type.Object({
  columns: Type.Array(ColumnSchema),
  rows: Type.Array(RowSchema),
  /**
   * Registry `identifier` of the field the columns are grouped by, e.g. `label`, or `null`
   * for a board with no grouping chosen yet (D29 / github/views#210). Explicitly stated
   * rather than absent so "not arranged yet" is a positive fact, matching `rowField` below.
   */
  columnField: Type.Union([Type.String(), Type.Null()]),
  /**
   * Same for rows, or `null` for a board with no swimlane (D29 / github/views#77).
   * Explicitly stated rather than absent so "no grouping" is a positive fact.
   */
  rowField: Type.Union([Type.String(), Type.Null()]),
  /**
   * The axis fields' own names. The field list is fetched separately and after the board is
   * drawn, so the identifiers above cannot be resolved to a name while an axis renders one.
   * Null exactly when the identifier beside it is.
   *
   * Optional so this client can be deployed before the server emits them, which is the order
   * the two are released in. Absent reads the same as null.
   */
  columnFieldLabel: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  rowFieldLabel: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  /** Only the populated pairs. An absent pair is an empty cell unless `cellsTruncated`. */
  cells: Type.Array(CellSchema),
  hasMoreColumns: Type.Boolean(),
  hasMoreRows: Type.Boolean(),
  columnsTruncated: Type.Boolean(),
  rowsTruncated: Type.Boolean(),
  /** Optional until the board distinguishes a truncated cell grid from an empty cell. */
  cellsTruncated: Type.Optional(Type.Boolean()),
  /**
   * Transcribed from the grouping field's own `countsOverlap` declaration — read once
   * off the field, never summed from the columns or recomputed here. A field placing
   * one item in several buckets says so once, on the field (github/views#55). `label`
   * and `review` are both overlapping registered fields; this flag copies whichever
   * produced the axis buckets (`columnField`, then `rowField`).
   */
  countsOverlap: Type.Boolean(),
  /** Opaque axis cursors reserved for pagination, which the board does not consume yet. */
  columnsCursor: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  rowsCursor: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  pageSize: Type.Optional(Type.Number()),
  /**
   * The repository-level writability fact, which gates whether the drag affordance renders at
   * all. Separate from a card's own `canMove` and not interchangeable with it: this one
   * decides whether any card may be picked up, that one decides which.
   *
   * Optional and absent-reads-as-`false`, for the reason `canMove` is.
   */
  viewerCanMoveItems: Type.Optional(Type.Boolean()),
  /**
   * What the server actually searched, after resolving the view's query and any edited one
   * previewed over it. A later page is drawn from this, since a cursor resumed under an edited
   * query, or one whose `sort:` differs, reads a different sequence than it was minted in.
   * Optional so this client can ship before the server states it.
   */
  query: Type.Optional(Type.String()),
})
export type BoardInitialPayload = Type.Static<typeof BoardInitialPayloadSchema>

export const boardInitialPayloadValidator = Compile(BoardInitialPayloadSchema)

/**
 * What a heading draws besides the count, keyed by bucket. Built from the payload's own axis
 * rather than from the cards in it, so a bucket nothing sits in is drawn the same as one that
 * is full, and a cell the board has not loaded costs nothing to read.
 *
 * One map per axis rather than one per kind of material: a bucket's colour, its markup and
 * whatever a field carries next all arrive together on `label`, and splitting them apart here
 * would put every field's buckets back into a row of optional side maps.
 */
export type BoardAxisLabels = ReadonlyMap<string, BoardPayloadAxisLabel>

export function axisLabels(axis: ReadonlyArray<{key: string; label: BoardPayloadAxisLabel}>): BoardAxisLabels {
  const labels = new Map<string, BoardPayloadAxisLabel>()
  for (const entry of axis) {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- `key` here is the axis entry's wire-contract key, not a keyboard event's
    labels.set(entry.key, entry.label)
  }
  return labels
}

/**
 * Runtime guard used to assert a parsed JSON value matches the contract. Importing JSON
 * widens string literals to `string`, so the `type` discriminant is only verifiable here.
 */
export function isBoardInitialPayload(value: unknown): value is BoardInitialPayload {
  return boardInitialPayloadValidator.Check(value)
}

/**
 * Maps the payload into the board's contract. Every stated fact carries across, except
 * `cellsTruncated` and `itemsLoaded`, which have no affordance to drive yet. Cursors and
 * axis-field identifiers do not either, because the board holds neither.
 */
export function toBoardData(payload: BoardInitialPayload): BoardData<BoardPayloadItem> {
  return {
    columns: payload.columns.map(column => ({
      key: column.key,
      label: column.label.text,
      totalCount: column.totalCount,
    })),
    rows: payload.rows.map(row => ({
      key: row.key,
      label: row.label.text,
      totalCount: row.totalCount,
    })),
    cells: payload.cells.map(cell => ({
      columnKey: cell.columnKey,
      rowKey: cell.rowKey,
      items: cell.items,
      totalCount: cell.totalCount,
      hasMoreItems: cell.hasMoreItems,
    })),
    hasMoreColumns: payload.hasMoreColumns,
    hasMoreRows: payload.hasMoreRows,
    columnsTruncated: payload.columnsTruncated,
    rowsTruncated: payload.rowsTruncated,
    countsOverlap: payload.countsOverlap,
    pageSize: payload.pageSize,
  }
}
